import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import * as React from 'react'
import { cn } from '@/lib/utils'
import { Spinner } from './spinner'

export const buttonVariants = cva(
  'relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap font-medium transition-[background,color,box-shadow,transform] duration-150 active:translate-y-px disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-brand text-brand-foreground shadow-[0_1px_0_0_rgb(255_255_255/0.12)_inset,0_6px_16px_-8px_color-mix(in_oklab,var(--brand)_70%,transparent)] hover:bg-brand-hover',
        secondary: 'bg-surface-2 text-foreground hover:bg-surface-3',
        outline: 'border border-border-strong bg-surface text-foreground shadow-soft hover:bg-surface-2',
        ghost: 'text-foreground hover:bg-surface-2',
        danger: 'bg-danger text-white hover:opacity-90 dark:text-black',
        'danger-outline': 'border border-danger-border bg-surface text-danger hover:bg-danger-soft',
        link: 'h-auto px-0 text-brand underline-offset-4 hover:underline',
      },
      size: {
        xs: 'h-7 rounded-md px-2 text-xs',
        sm: 'h-8 rounded-md px-3 text-[13px]',
        md: 'h-9 rounded-lg px-3.5 text-sm',
        lg: 'h-11 rounded-xl px-5 text-[15px]',
        xl: 'h-13 rounded-2xl px-7 text-base',
        icon: 'size-9 rounded-lg',
        'icon-sm': 'size-8 rounded-md',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

export function Button({ className, variant, size, asChild = false, loading, children, disabled, ...props }: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button'
  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading && (
            <span className="absolute inset-0 grid place-items-center">
              <Spinner className="size-4" />
            </span>
          )}
          <span className={cn('inline-flex items-center gap-2', loading && 'invisible')}>{children}</span>
        </>
      )}
    </Comp>
  )
}
